import { useRef, useState } from "react";

import UnknownUserImage from "@/components/common/UnknownUserImage";
import ErrorMessage from "@/components/error-message";
import { isImage } from "@/helpers";

interface AccountImageUploadProps {
  // The photo already on the account arrives as its URL. Once the visitor picks
  // a file, value is that File and new_upload carries the preview instead.
  value: File | string | null;
  onChange: (photo: File | null) => void;
}

export default function AccountImageUpload({ value, onChange }: AccountImageUploadProps) {
  const [error, setError] = useState<string | null>(null);
  const [new_upload, setNewUpload] = useState<string | null>(null);
  const image_input_ref = useRef<HTMLInputElement>(null);

  const _clearImgInput = () => {
    setError(null);
    setNewUpload(null);
    onChange(null);
    if (image_input_ref.current) {
      image_input_ref.current.value = "";
    }
  };

  const _uploadImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type || file.type.indexOf("image") === -1 || !isImage(file.type)) {
      setError(`File is not an image image format: ${file.type}`);
      setNewUpload(null);
      return;
    }
    if (file.size > 500000) {
      // 5MB
      setError("File size larger than 5MB");
      setNewUpload(null);
      return;
    }
    onChange(file);
    const onLoad = (load_event: ProgressEvent<FileReader>) => {
      const result = load_event.target?.result;
      setNewUpload(typeof result === "string" ? result : null);
      setError(null);
    };
    const reader = new FileReader();
    reader.addEventListener("load", onLoad, { once: true });
    reader.readAsDataURL(file);
  };

  const existing_photo_url = typeof value === "string" ? value : null;
  const preview_src = new_upload || existing_photo_url;

  return (
    <div>
      <div className="flex">
        <input
          type="file"
          className="w-64"
          onChange={_uploadImage}
          accept="image/jpeg, image/png, image/jpg"
          ref={image_input_ref}
        />
        <button
          type="button"
          onClick={_clearImgInput}
          className="material-symbols-rounded notranslate h-8 w-8 cursor-pointer rounded-[50%] p-1 hover:bg-[color:rgba(255,255,255,0.05)]"
          title="Remove image"
          aria-label="Remove image"
        >
          close
        </button>
      </div>
      <ErrorMessage message={error} />
      <div className="my-4">
        {preview_src ? (
          <img
            src={preview_src}
            className="mx-auto h-[10rem] w-[10rem] rounded-[50%] object-cover"
            alt="Account avatar preview"
          />
        ) : (
          <UnknownUserImage style="mx-auto h-[10rem] w-[10rem] rounded-[50%] object-cover" />
        )}
      </div>
    </div>
  );
}
